import { apiService } from './ApiService';
import { AxiosResponse } from 'axios';

// ---------------------------------------------------------------------------
// State machine config types
// ---------------------------------------------------------------------------

export interface WorkflowState {
  key: string;
  label: string;
  is_initial: boolean;
  is_terminal: boolean;
}

export interface WorkflowTransition {
  id: string;
  from_state: string;
  to_state: string;
  label: string;
  allowed_actors: string[];
}

export interface StateConfigResponse {
  states: WorkflowState[];
  transitions: WorkflowTransition[];
  version: number;
  is_default: boolean;
}

// ---------------------------------------------------------------------------
// Legacy linear-step types (kept for backwards compatibility)
// ---------------------------------------------------------------------------

export interface ResultRule {
  field: string;
  operator: string;
  value?: any;
  action: string;
  status?: string;
  skip_count?: number;
  label?: string;
}

export interface WorkflowStep {
  type: string;
  config: Record<string, any>;
  on_error?: string;
  error_status?: string;
}

export interface WorkflowTrigger {
  type: string;
  from_status?: string;
  to_status?: string;
}

export interface WorkflowDefinition {
  id: number;
  name: string;
  description?: string;
  is_active: boolean;
  trigger: WorkflowTrigger;
  steps: WorkflowStep[];
  created_at: string;
  updated_at: string;
}

export interface WorkflowExecution {
  id: number;
  definition_id: number;
  order_id: number;
  status: 'running' | 'completed' | 'failed' | 'skipped';
  results: Array<{ step: number; type: string; [key: string]: any }>;
  error_message?: string;
  started_at: string;
  finished_at?: string;
}

export interface DefinitionCreateRequest {
  name: string;
  description?: string;
  trigger: WorkflowTrigger;
  steps: WorkflowStep[];
  is_active?: boolean;
}

export interface DefinitionPatchRequest {
  name?: string;
  description?: string;
  trigger?: WorkflowTrigger;
  steps?: WorkflowStep[];
  is_active?: boolean;
}

// ---------------------------------------------------------------------------
// DAG workflow types
// ---------------------------------------------------------------------------

export interface DAGEdgeCondition {
  field: string;
  operator: string;
  value?: any;
}

export interface DAGEdge {
  id: string;
  source: string;
  target: string;
  condition?: DAGEdgeCondition;
  label?: string;
}

export interface DAGNode {
  id: string;
  label: string;
  type: string;           // run_integration | entity_action | foreach_array | send_notification | condition
  config: Record<string, any>;
  on_error?: string;      // abort | continue | set_status
  error_status?: string;
  // visual position (stored in config.position)
  position?: { x: number; y: number };
}

export interface DAGTrigger {
  type: string;
  filters: Record<string, any>;
  // cron-specific fields (only present when type === "cron")
  schedule?: string;
  timezone?: string;
  entity_type?: string;
  entity_id?: string;
  payload?: Record<string, any>;
  // subflow-specific (only present when type === "subflow"): inputs this
  // workflow expects from the calling parent — drives the variable picker
  expected_inputs?: Array<{ key: string; description?: string }>;
  /** Shared workflows only: run for every organisation (narrowed by filters). */
  all_organizations?: boolean;
}

export interface SecretRef {
  name: string;        // variable name exposed in the DAG as secrets.<name>
  secret: string;      // Google Secret Manager secret id
  version?: string;    // defaults to "latest"
  project?: string;    // defaults to the runtime GCP project
}

export interface DAGDefinition {
  id: number;
  name: string;
  description?: string;
  is_active: boolean;
  organization_id?: number | null;
  organization_ids?: number[];
  secrets?: SecretRef[];
  trigger: DAGTrigger;
  nodes: DAGNode[];
  edges: DAGEdge[];
  created_at: string;
  updated_at: string;
}

export interface DAGExecution {
  id: number;
  definition_id: number;
  entity_type: string;
  entity_id?: string;
  event_type: string;
  event_payload: Record<string, any>;
  status: 'running' | 'completed' | 'failed';
  node_results: Record<string, any>;
  error_message?: string;
  started_at: string;
  finished_at?: string;
}

export interface DAGDefinitionCreateRequest {
  name: string;
  description?: string;
  trigger: DAGTrigger;
  nodes: DAGNode[];
  edges: DAGEdge[];
  is_active?: boolean;
  organization_ids?: number[];
  secrets?: SecretRef[];
}

export interface DAGDefinitionPatchRequest {
  name?: string;
  description?: string;
  trigger?: DAGTrigger;
  nodes?: DAGNode[];
  edges?: DAGEdge[];
  is_active?: boolean;
  organization_ids?: number[];
  secrets?: SecretRef[];
}

export interface EntityFieldInfo {
  path: string;
  type: string;
  description: string;
}

export interface FilterFieldDef {
  key: string;
  label: string;
  type: 'status' | 'text' | 'number';
  ops: string[];
  description?: string;
}

export interface EventTypeInfo {
  type: string;
  label: string;
  description: string;
  entity_type: string;           // e.g. "order" | "contact" | "vehicle"
  entity_fields: EntityFieldInfo[];  // fields available as entity.* in the DAG context
  payload_schema: Record<string, any>;
  filter_fields: FilterFieldDef[];
  triggered_item_fields?: EntityFieldInfo[];  // fields available as triggered_item.* (e.g. item_added_to_cart)
  trigger_config_schema?: Record<string, { type: string; required?: boolean; description: string }>;
}

export interface NodeTypeInfo {
  type: string;
  label: string;
  description: string;
  config_schema: Record<string, any>;
}

export interface RunPythonTestResult {
  success: boolean;
  output?: any;
  logs?: string | null;
  error?: string;
  duration_ms?: number;
}

export interface RunPythonGenerateResult {
  code: string;
  explanation: string;
  syntax_ok: boolean;
  syntax_error?: string | null;
  model?: string;
}

/** One saved version of a workflow (version history). */
export interface DAGVersionSummary {
  version: number;
  note?: string | null;
  created_by?: string | null;
  created_at?: string | null;
  name?: string;
  steps: number;
  arrows: number;
}

export interface DAGVersion extends DAGVersionSummary {
  snapshot: {
    name?: string;
    description?: string | null;
    trigger?: DAGTrigger;
    nodes?: DAGNode[];
    edges?: DAGEdge[];
    organization_ids?: number[] | null;
    secrets?: SecretRef[] | null;
  };
}

/** A reusable set of steps saved to the organisation's library. */
export interface StepLibraryItem {
  id: number;
  name: string;
  description?: string | null;
  created_by?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  step_count: number;
  step_types: string[];
  /** Present when fetched one by one: a steps export (format "catax-workflow-steps"). */
  steps?: Record<string, any>;
}

/** Order + items to pick for a builder test run. */
export interface DryRunOrder {
  order_id: number;
  external_order_id?: string | null;
  organization_id?: number | null;
  vin?: string | null;
  items: Array<{ index: number; item_id: string | null; part_number?: string | null; name?: string | null }>;
}

export interface DryRunStep {
  id: string;
  label: string;
  type: string;
  /** ran = executed · simulated = write step, not executed · skipped = no arrow allowed it · error */
  state: 'ran' | 'simulated' | 'skipped' | 'error';
  duration_ms?: number;
  error?: string | null;
}

export interface DryRunResult {
  status: 'completed' | 'failed' | 'timeout';
  error?: string | null;
  steps: DryRunStep[];
  node_results: Record<string, any>;
  event_payload?: Record<string, any>;
}

export interface DryRunRequest {
  definition_id?: number;
  trigger: DAGTrigger | Record<string, any>;
  nodes: DAGNode[];
  edges: DAGEdge[];
  secrets?: SecretRef[];
  order_id?: number;
  item_id?: string;
  event_payload?: Record<string, any>;
}

/** Non-blocking finding from the backend workflow checks (dag_checks.py). */
export interface DAGCheckWarning {
  code: 'missing_step' | 'not_upstream' | 'competing_writes' | string;
  node_id?: string;
  other_node_id?: string;
  edge_id?: string;
  ref?: string;
  message: string;
}

export interface DAGValidateResult {
  valid: boolean;
  errors: string[];
  /** Absent on backends older than the checks. */
  warnings?: DAGCheckWarning[];
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

/** ?org_id=…&shared=true query string used by the org-scoped workflow endpoints. */
function scopeQs(orgId?: number, shared?: boolean): string {
  const params = new URLSearchParams();
  if (orgId !== undefined) params.set('org_id', String(orgId));
  if (shared) params.set('shared', 'true');
  return params.toString() ? `?${params}` : '';
}

export class WorkflowService {
  private static instance: WorkflowService;

  public static getInstance(): WorkflowService {
    if (!WorkflowService.instance) {
      WorkflowService.instance = new WorkflowService();
    }
    return WorkflowService.instance;
  }

  // ---- State machine config ------------------------------------------------

  getStateConfig(orgId?: number, shared?: boolean): Promise<AxiosResponse<StateConfigResponse>> {
    const qs = orgId ? `?org_id=${orgId}` : shared ? '?shared=true' : '';
    return apiService.get(`/api/workflows/state-config${qs}`);
  }

  saveStateConfig(data: { states: WorkflowState[]; transitions: WorkflowTransition[] }, orgId?: number, shared?: boolean): Promise<AxiosResponse<StateConfigResponse>> {
    const qs = orgId ? `?org_id=${orgId}` : shared ? '?shared=true' : '';
    return apiService.put(`/api/workflows/state-config${qs}`, data);
  }

  addState(state: Omit<WorkflowState, 'id'>, orgId?: number, shared?: boolean): Promise<AxiosResponse<StateConfigResponse>> {
    const qs = orgId ? `?org_id=${orgId}` : shared ? '?shared=true' : '';
    return apiService.post(`/api/workflows/state-config/states${qs}`, state);
  }

  deleteState(key: string, orgId?: number, shared?: boolean): Promise<AxiosResponse<StateConfigResponse>> {
    const qs = orgId ? `?org_id=${orgId}` : shared ? '?shared=true' : '';
    return apiService.delete(`/api/workflows/state-config/states/${key}${qs}`);
  }

  addTransition(transition: Omit<WorkflowTransition, 'id'>, orgId?: number, shared?: boolean): Promise<AxiosResponse<StateConfigResponse>> {
    const qs = orgId ? `?org_id=${orgId}` : shared ? '?shared=true' : '';
    return apiService.post(`/api/workflows/state-config/transitions${qs}`, transition);
  }

  deleteTransition(transitionId: string, orgId?: number, shared?: boolean): Promise<AxiosResponse<StateConfigResponse>> {
    const qs = orgId ? `?org_id=${orgId}` : shared ? '?shared=true' : '';
    return apiService.delete(`/api/workflows/state-config/transitions/${transitionId}${qs}`);
  }

  resetStateConfig(orgId?: number, shared?: boolean): Promise<AxiosResponse<StateConfigResponse>> {
    const qs = orgId ? `?org_id=${orgId}` : shared ? '?shared=true' : '';
    return apiService.post(`/api/workflows/state-config/reset${qs}`, {});
  }

  getAllowedTransitions(currentStatus: string, actor = 'user'): Promise<AxiosResponse<{ current_status: string; allowed_next_states: string[] }>> {
    return apiService.get(`/api/workflows/state-config/allowed-transitions?current_status=${currentStatus}&actor=${actor}`);
  }

  // ---- Legacy linear-step definitions (kept for migration path) ------------

  listDefinitions(): Promise<AxiosResponse<WorkflowDefinition[]>> {
    return apiService.get('/api/workflows/definitions');
  }

  createDefinition(data: DefinitionCreateRequest): Promise<AxiosResponse<WorkflowDefinition>> {
    return apiService.post('/api/workflows/definitions', data);
  }

  getDefinition(id: number): Promise<AxiosResponse<WorkflowDefinition>> {
    return apiService.get(`/api/workflows/definitions/${id}`);
  }

  updateDefinition(id: number, data: DefinitionPatchRequest): Promise<AxiosResponse<WorkflowDefinition>> {
    return apiService.patch(`/api/workflows/definitions/${id}`, data);
  }

  deleteDefinition(id: number): Promise<AxiosResponse<void>> {
    return apiService.delete(`/api/workflows/definitions/${id}`);
  }

  getExecutionsForDefinition(definitionId: number): Promise<AxiosResponse<WorkflowExecution[]>> {
    return apiService.get(`/api/workflows/definitions/${definitionId}/executions`);
  }

  getExecutionsForOrder(orderId: number): Promise<AxiosResponse<WorkflowExecution[]>> {
    return apiService.get(`/api/workflows/definitions/executions/order/${orderId}`);
  }

  // ---- DAG definitions -----------------------------------------------------

  listDAGDefinitions(orgId?: number, shared?: boolean): Promise<AxiosResponse<DAGDefinition[]>> {
    const params = new URLSearchParams();
    if (orgId !== undefined) params.set('org_id', String(orgId));
    if (shared) params.set('shared', 'true');
    const qs = params.toString() ? `?${params}` : '';
    return apiService.get(`/api/workflows/dag/definitions${qs}`);
  }

  createDAGDefinition(data: DAGDefinitionCreateRequest, orgId?: number, shared?: boolean): Promise<AxiosResponse<DAGDefinition>> {
    const params = new URLSearchParams();
    if (orgId !== undefined) params.set('org_id', String(orgId));
    if (shared) params.set('shared', 'true');
    const qs = params.toString() ? `?${params}` : '';
    return apiService.post(`/api/workflows/dag/definitions${qs}`, data);
  }

  getDAGDefinition(id: number, orgId?: number, shared?: boolean): Promise<AxiosResponse<DAGDefinition>> {
    const params = new URLSearchParams();
    if (orgId !== undefined) params.set('org_id', String(orgId));
    if (shared) params.set('shared', 'true');
    const qs = params.toString() ? `?${params}` : '';
    return apiService.get(`/api/workflows/dag/definitions/${id}${qs}`);
  }

  updateDAGDefinition(id: number, data: DAGDefinitionPatchRequest, orgId?: number, shared?: boolean): Promise<AxiosResponse<DAGDefinition>> {
    const params = new URLSearchParams();
    if (orgId !== undefined) params.set('org_id', String(orgId));
    if (shared) params.set('shared', 'true');
    const qs = params.toString() ? `?${params}` : '';
    return apiService.patch(`/api/workflows/dag/definitions/${id}${qs}`, data);
  }

  deleteDAGDefinition(id: number, orgId?: number, shared?: boolean): Promise<AxiosResponse<void>> {
    const params = new URLSearchParams();
    if (orgId !== undefined) params.set('org_id', String(orgId));
    if (shared) params.set('shared', 'true');
    const qs = params.toString() ? `?${params}` : '';
    return apiService.delete(`/api/workflows/dag/definitions/${id}${qs}`);
  }

  // ── Step library ─────────────────────────────────────────────────────────
  listStepLibrary(orgId?: number, shared?: boolean): Promise<AxiosResponse<StepLibraryItem[]>> {
    return apiService.get(`/api/workflows/dag/step-library${scopeQs(orgId, shared)}`);
  }

  getStepLibraryItem(id: number, orgId?: number, shared?: boolean): Promise<AxiosResponse<StepLibraryItem>> {
    return apiService.get(`/api/workflows/dag/step-library/${id}${scopeQs(orgId, shared)}`);
  }

  saveStepLibraryItem(
    data: { name: string; description?: string; steps: Record<string, any> }, orgId?: number, shared?: boolean,
  ): Promise<AxiosResponse<StepLibraryItem>> {
    return apiService.post(`/api/workflows/dag/step-library${scopeQs(orgId, shared)}`, data);
  }

  deleteStepLibraryItem(id: number, orgId?: number, shared?: boolean): Promise<AxiosResponse<void>> {
    return apiService.delete(`/api/workflows/dag/step-library/${id}${scopeQs(orgId, shared)}`);
  }

  // ── Test runs (nothing is written) ──────────────────────────────────────
  findDryRunOrder(order: string): Promise<AxiosResponse<DryRunOrder>> {
    return apiService.get(`/api/workflows/dag/dry-run/order-items?order=${encodeURIComponent(order)}`);
  }

  dryRunDAG(body: DryRunRequest, orgId?: number, shared?: boolean): Promise<AxiosResponse<DryRunResult>> {
    return apiService.post(`/api/workflows/dag/dry-run${scopeQs(orgId, shared)}`, body);
  }

  // ── Version history ──────────────────────────────────────────────────────
  listDAGVersions(id: number, orgId?: number, shared?: boolean): Promise<AxiosResponse<DAGVersionSummary[]>> {
    return apiService.get(`/api/workflows/dag/definitions/${id}/versions${scopeQs(orgId, shared)}`);
  }

  getDAGVersion(id: number, version: number, orgId?: number, shared?: boolean): Promise<AxiosResponse<DAGVersion>> {
    return apiService.get(`/api/workflows/dag/definitions/${id}/versions/${version}${scopeQs(orgId, shared)}`);
  }

  restoreDAGVersion(id: number, version: number, orgId?: number, shared?: boolean): Promise<AxiosResponse<DAGDefinition>> {
    return apiService.post(`/api/workflows/dag/definitions/${id}/versions/${version}/restore${scopeQs(orgId, shared)}`, {});
  }

  getDAGExecutions(definitionId: number, limit = 50, orgId?: number, shared?: boolean): Promise<AxiosResponse<DAGExecution[]>> {
    const params = new URLSearchParams({ limit: String(limit) });
    if (orgId !== undefined) params.set('org_id', String(orgId));
    if (shared) params.set('shared', 'true');
    return apiService.get(`/api/workflows/dag/definitions/${definitionId}/executions?${params}`);
  }

  getDAGExecutionsForEntity(entityType: string, entityId: string, limit = 50): Promise<AxiosResponse<DAGExecution[]>> {
    return apiService.get(`/api/workflows/dag/executions/entity/${entityType}/${entityId}?limit=${limit}`);
  }

  manuallyTriggerDAG(definitionId: number, eventPayload: Record<string, any> = {}, orgId?: number, shared?: boolean): Promise<AxiosResponse<{ status: string; definition_id: number }>> {
    const params = new URLSearchParams();
    if (orgId !== undefined) params.set('org_id', String(orgId));
    if (shared) params.set('shared', 'true');
    const qs = params.toString() ? `?${params}` : '';
    return apiService.post(`/api/workflows/dag/definitions/${definitionId}/trigger${qs}`, { event_payload: eventPayload });
  }

  validateDAG(nodes: DAGNode[], edges: DAGEdge[]): Promise<AxiosResponse<DAGValidateResult>> {
    return apiService.post('/api/workflows/dag/validate', { nodes, edges });
  }

  /**
   * Execute a run_python snippet once against sample inputs — no workflow, no
   * persistence. Backs the inline "Run" / debug button in the node editor.
   */
  testRunPython(body: {
    code: string;
    inputs?: Record<string, any>;
    context?: Record<string, any>;
    timeout_seconds?: number;
  }): Promise<AxiosResponse<RunPythonTestResult>> {
    return apiService.post('/api/workflows/dag/run-python/test', body);
  }

  /**
   * Generate (or modify) a run_python snippet from a natural-language prompt.
   * The backend system-prompts the sandbox contract so the generated code fits
   * the node's boundaries. Backs the "Generate with AI" panel in the editor.
   */
  generateRunPython(body: {
    prompt: string;
    current_code?: string;
    input_keys?: string[];
    context_vars?: string[];
  }): Promise<AxiosResponse<RunPythonGenerateResult>> {
    return apiService.post('/api/workflows/dag/run-python/generate', body);
  }

  /**
   * Store a secret value in Google Secret Manager and return a reference
   * (variable name → secret id) to bind into a workflow definition.
   * The plaintext value is sent once and never persisted in our database.
   */
  createWorkflowSecret(
    body: { name: string; value: string; secret?: string; project?: string },
    orgId?: number,
  ): Promise<AxiosResponse<SecretRef>> {
    const params = new URLSearchParams();
    if (orgId !== undefined) params.set('org_id', String(orgId));
    const qs = params.toString() ? `?${params}` : '';
    return apiService.post(`/api/workflows/dag/secrets${qs}`, body);
  }

  /**
   * List secrets previously created from a workflow that can be reused.
   * Only references are returned — never the secret values.
   */
  listWorkflowSecrets(orgId?: number): Promise<AxiosResponse<SecretRef[]>> {
    const params = new URLSearchParams();
    if (orgId !== undefined) params.set('org_id', String(orgId));
    const qs = params.toString() ? `?${params}` : '';
    return apiService.get(`/api/workflows/dag/secrets${qs}`);
  }

  // ---- DAG registry --------------------------------------------------------

  getEventTypes(): Promise<AxiosResponse<EventTypeInfo[]>> {
    return apiService.get('/api/workflows/dag/event-types');
  }

  getNodeTypes(): Promise<AxiosResponse<NodeTypeInfo[]>> {
    return apiService.get('/api/workflows/dag/node-types');
  }
}

export const workflowService = WorkflowService.getInstance();
